import {createApp} from 'vue'
import Buefy from 'buefy'
import './simple-layout.js'
import {API_URL, buefyConfig, fetchJson, getDefaultLocale, getTimezone, isEmpty, isInsideIFrame} from "./utils";


if(isInsideIFrame()) {
    // If this page is displayed within an IFrame, open the sign-in page in a popup for security reasons
    // An external website might for example add a key listener to the iframe content
    window.alert('Authentication within an IFrame is forbidden for security reasons. Please use `?display=popup` to trigger authentication in a popup.')
}
else {
    init();
}

async function init() {

    const [status, signInChoices] = await Promise.all([
        fetchJson(API_URL + '/status'),
        fetchJson(API_URL + '/sign-in-choices')
    ]);

    const timezone = getTimezone();


    if (signInChoices.provider) {
        console.log(`Signing in using provider "${signInChoices.provider}`)
        window.location.href = `${API_URL}/sign-in?provider=${encodeURIComponent(signInChoices.provider)}&timezone=${encodeURIComponent(timezone)}`
    }



    const locale = signInChoices.locale ?? getDefaultLocale();
    const messages = await fetchJson(`./locales/sign-in-${locale}.json`);
    document.documentElement.lang = locale;



    async function loginIxSSO() {
        // see EAUTH2-16
        console.log("Signing in using legacy SSO")
        const res = await fetch('../../bypsauth/auth?streamversion=9.00.000.000&responseformat=byps-json&language=' + locale)
        if(res.ok) {
            // To resume the redirection flow to the client (possibly code exchange flow)
            window.location.href = API_URL + "/check-roles"
        }
        else {
            console.warn('Failed to sign-in with SSO')
            window.alert(await res.text())
        }
    }

    if (signInChoices.ixSsoEnabled && !signInChoices.enablePasswordLogin && isEmpty(signInChoices.providerLabels)) {
        // no other choice than legacy SSO? => trigger it directly
        await loginIxSSO()
    }

    const app = createApp({
        data() {
            return {
                messages,
                status,
                signInChoices,
                username: signInChoices.login_hint ?? '',
                password: '',
                changePassword: null, // 'MUST_CHANGE', 'SHOULD_CHANGE' or 'EXPIRED'
                newPassword: '',
                newPasswordRepeat: '',
                errorMessage: null,
                errorMessageSSO: null,
            }
        },
        computed: {
            hasProviders() {
                return !isEmpty(this.signInChoices.providerLabels) || this.signInChoices.ixSsoEnabled
            }
        },
        mounted() {
            if(signInChoices.enablePasswordLogin && !this.hasProviders) {
                // EAUTH2-45: focus on username/password field
                try{
                    if(signInChoices.login_hint)
                        this.$refs.password.focus();
                    else
                        this.$refs.username.focus();
                }
                catch(e) {

                }
            }
        },
        methods: {
            async eloLogin() {
                this.errorMessage = null

                const username = this.username
                const password = this.password
                const res = await fetch(API_URL + "/elo-login", {
                    method: 'POST',
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        username,
                        password,
                        timezone
                    })
                })
                if (res.ok) {
                    let json = await res.json()
                    if (json.changePassword === 'NO_CHANGE') {
                        // To resume the redirection flow to the client (possibly code exchange flow)
                        window.location.href = API_URL + "/check-roles"
                    } else {
                        // Currently, a failed login is still passed as OK
                        // because failures cannot provide a JSON response with more details
                        // since the IX REST Plugin removes any payloads for error responses.
                        this.changePassword = json.changePassword;
                        this.passwordRules = json.passwordRules;
                    }
                } else {
                    console.warn(`Failed to sign-in (${res.statusText})`)
                    if (res.status === 401)
                        this.errorMessage = messages['error_wrong_credentials'] ?? 'Unknown user, incorrect password, or the account is locked.';
                    else if (res.status === 409) // due to user configuration mistakes
                        this.errorMessage = messages['error_password_conflict'] ?? 'Conflict: password change required but insufficient rights to do so.';
                    else
                        this.errorMessage = messages['error_unexpected'] ?? 'Unexpected error, please contact your administrator.';
                }
            },
            signInWith(providerId) {
                window.location.href = `${API_URL}/sign-in?provider=${encodeURIComponent(providerId)}&timezone=${encodeURIComponent(timezone)}`;
            },
            async saveNewPassword() {
                this.errorMessage = null

                const username = this.username
                const password = this.password
                const newPassword = this.newPassword
                const newPasswordRepeat = this.newPasswordRepeat

                const res = await fetch(API_URL + "/change-password", {
                    method: 'POST',
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        username,
                        password,
                        timezone,
                        newPassword,
                        newPasswordRepeat
                    })
                })
                if (res.ok) {
                    // To resume the redirection flow to the client (possibly code exchange flow)
                    window.location.href = API_URL + "/check-roles"
                } else {
                    console.warn('Failed to change password')
                    if (res.status === 401) // should never happen
                        this.errorMessage = messages['error_wrong_credentials'] ?? 'Unknown user, incorrect password, or the account is locked.';
                    else if (res.status === 403)
                        this.errorMessage = messages['error_new_password_rejected'] ?? 'The new password does not match the password policy.';
                    else if (res.status === 409) // should never happen
                        this.errorMessage = messages['error_password_conflict'] ?? 'Conflict: password change required but insufficient rights to do so.';
                    else
                        this.errorMessage = messages['error_unexpected'] ?? 'Unexpected error, please contact your administrator.';

                }
            },
            async skipChangePassword() {
                // To resume the redirection flow to the client (possibly code exchange flow)
                window.location.href = API_URL + "/check-roles"
            },
            loginIxSSO
        }
    });
    app.use(Buefy, buefyConfig);
    app.mount('#app');

    console.log('Auth2 - Sign-in page loaded')
}
